import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Flex, Text, Button, Input, Select, HStack, VStack, FormControl, FormLabel,
  NumberInput, NumberInputField, Switch, FormHelperText,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useMICUTheme } from '../lib/micu/MICUThemeProvider';
import { db, savePatient, getPatient } from '../lib/micu/micuDatabase';
import { calculateIBW, lbsToKg, feetInchesToInches } from '../lib/micu/calculations';

const MICUPatient = () => {
  const { id } = useParams();
  const caseId = Number(id);
  const navigate = useNavigate();
  const { isDark } = useMICUTheme();

  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [ageUnit, setAgeUnit] = useState('years');
  const [heightMode, setHeightMode] = useState('inches'); // 'inches' or 'ftin'
  const [heightInches, setHeightInches] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weightMode, setWeightMode] = useState('kg');
  const [weightValue, setWeightValue] = useState('');
  const [caseName, setCaseName] = useState('');

  useEffect(() => {
    (async () => {
      const c = await db.cases.get(caseId);
      if (c) setCaseName(c.name);
      const p = await getPatient(caseId);
      if (p) {
        setGender(p.gender || '');
        setAge(p.age?.toString() || '');
        setAgeUnit(p.ageUnit || 'years');
        setHeightInches(p.heightInches?.toString() || '');
        if (p.weightKg) setWeightValue(p.weightKg.toString());
      }
    })();
  }, [caseId]);

  const getTotalInches = () => {
    if (heightMode === 'ftin') {
      return feetInchesToInches(Number(heightFeet) || 0, Number(heightIn) || 0);
    }
    return Number(heightInches) || 0;
  };

  const getWeightKg = () => {
    const val = Number(weightValue) || 0;
    return weightMode === 'lbs' ? lbsToKg(val) : val;
  };

  const handleSave = async () => {
    const totalInches = getTotalInches();
    const weightKg = getWeightKg();
    const ibwKg = gender && totalInches > 0 ? calculateIBW(gender, totalInches) : null;

    await savePatient(caseId, {
      gender,
      age: Number(age) || null,
      ageUnit,
      heightInches: totalInches || null,
      weightKg: weightKg || null,
      ibwKg,
    });

    navigate(`/micu-mobile/case/${caseId}`);
  };

  const ibwPreview = gender && getTotalInches() > 0 ? calculateIBW(gender, getTotalInches()) : null;

  return (
    <Box p={4}>
      <HStack spacing={2} mb={4}>
        <Button variant="micuGhost" size="sm" leftIcon={<ArrowBackIcon />} onClick={() => navigate(`/micu-mobile/new`)}>
          Back
        </Button>
        <Text fontSize="sm" color="micu.textMuted">{caseName}</Text>
      </HStack>

      <Text fontSize="xl" fontWeight="bold" color="micu.text" mb={6}>Patient Demographics</Text>

      <VStack spacing={5} align="stretch">
        <FormControl>
          <FormLabel color="micu.textSecondary" fontSize="sm">Gender</FormLabel>
          <Select
            variant="micu"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            placeholder="Select gender"
          >
            <option value="M">Male</option>
            <option value="F">Female</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel color="micu.textSecondary" fontSize="sm">Age</FormLabel>
          <HStack>
            <NumberInput min={0} max={120} value={age} onChange={(v) => setAge(v)} flex={1}>
              <NumberInputField variant="micu" placeholder="Age" bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} border="1px solid" borderColor="micu.border" color="micu.text" />
            </NumberInput>
            <Select variant="micu" value={ageUnit} onChange={(e) => setAgeUnit(e.target.value)} w="120px">
              <option value="years">Years</option>
              <option value="months">Months</option>
            </Select>
          </HStack>
        </FormControl>

        <FormControl>
          <Flex justify="space-between" align="center" mb={2}>
            <FormLabel color="micu.textSecondary" fontSize="sm" mb={0}>Height</FormLabel>
            <HStack spacing={2}>
              <Text fontSize="xs" color="micu.textMuted">ft/in</Text>
              <Switch
                size="sm"
                isChecked={heightMode === 'ftin'}
                onChange={() => setHeightMode(heightMode === 'inches' ? 'ftin' : 'inches')}
              />
            </HStack>
          </Flex>
          {heightMode === 'inches' ? (
            <NumberInput min={0} max={96} value={heightInches} onChange={(v) => setHeightInches(v)}>
              <NumberInputField variant="micu" placeholder="Height in inches" bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} border="1px solid" borderColor="micu.border" color="micu.text" />
            </NumberInput>
          ) : (
            <HStack>
              <NumberInput min={0} max={8} value={heightFeet} onChange={(v) => setHeightFeet(v)} flex={1}>
                <NumberInputField variant="micu" placeholder="Feet" bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} border="1px solid" borderColor="micu.border" color="micu.text" />
              </NumberInput>
              <Text color="micu.textMuted">ft</Text>
              <NumberInput min={0} max={11} value={heightIn} onChange={(v) => setHeightIn(v)} flex={1}>
                <NumberInputField variant="micu" placeholder="Inches" bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} border="1px solid" borderColor="micu.border" color="micu.text" />
              </NumberInput>
              <Text color="micu.textMuted">in</Text>
            </HStack>
          )}
        </FormControl>

        <FormControl>
          <Flex justify="space-between" align="center" mb={2}>
            <FormLabel color="micu.textSecondary" fontSize="sm" mb={0}>Weight</FormLabel>
            <HStack spacing={2}>
              <Text fontSize="xs" color={weightMode === 'kg' ? 'micu.teal' : 'micu.textMuted'} cursor="pointer" onClick={() => setWeightMode('kg')}>kg</Text>
              <Text fontSize="xs" color="micu.textMuted">/</Text>
              <Text fontSize="xs" color={weightMode === 'lbs' ? 'micu.teal' : 'micu.textMuted'} cursor="pointer" onClick={() => setWeightMode('lbs')}>lbs</Text>
            </HStack>
          </Flex>
          <NumberInput min={0} max={500} value={weightValue} onChange={(v) => setWeightValue(v)}>
            <NumberInputField variant="micu" placeholder={`Weight in ${weightMode}`} bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} border="1px solid" borderColor="micu.border" color="micu.text" />
          </NumberInput>
          {weightMode === 'lbs' && weightValue && (
            <FormHelperText color="micu.textMuted" fontSize="xs">
              = {lbsToKg(Number(weightValue))} kg
            </FormHelperText>
          )}
        </FormControl>

        {ibwPreview && (
          <Box p={3} bg={isDark ? 'micu.bgTertiary' : 'micu.bgSecondary'} borderRadius="md" border="1px solid" borderColor="micu.border">
            <Text fontSize="sm" color="micu.textSecondary">
              IBW: <Text as="span" fontWeight="bold" color="micu.teal">{ibwPreview} kg</Text>
              <Text as="span" fontSize="xs" color="micu.textMuted" ml={2}>
                ({gender === 'M' ? '50' : '45.5'} + 2.3 x ({getTotalInches()} - 60))
              </Text>
            </Text>
          </Box>
        )}
      </VStack>

      <Flex gap={3} mt={8}>
        <Button variant="micuGhost" flex={1} onClick={() => navigate('/micu-mobile/new')}>Back</Button>
        <Button variant="micuPrimary" flex={1} onClick={handleSave}>Next</Button>
      </Flex>
    </Box>
  );
};

export default MICUPatient;
